/*
 * Read-more for the guide grid.
 *
 * The copy behind each entry lives in the theme now, so there is no page to
 * send anyone to. Expanding inside the card was the obvious answer and it
 * looked wrong: one 300px column ran seven hundred pixels down the page while
 * its three neighbours sat as holes beside it.
 *
 * So the card stays clamped, and the full copy opens in a panel spanning the
 * whole grid, tucked in after the last card of the row that was clicked —
 * picture on the left, copy at a readable measure on the right. Nothing in the
 * grid moves sideways, and the row of shapes stays comparable while you read.
 *
 * The panel is built from the card, so nothing is duplicated in the markup,
 * and the clamp is applied here rather than in the markup so the copy stays
 * whole and readable if the script never runs.
 */
(function () {
  // A page carries one of these tags per guide band, so the file is executed
  // once per band. Without this the click below would be handled once per
  // band too, and an even number of bands would toggle back to where it
  // started — the button would look dead.
  if (window.zkgGuideGridLoaded) return;
  window.zkgGuideGridLoaded = true;

  const CLAMP = 'zkg-entry__body--clamped';
  const TOGGLE = '.zkg-entry__more[aria-controls]';
  const OPEN = 'zkg-entry--open';

  let openButton = null;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ clamping
     An entry only gets a button once it is measured and found to be hiding
     something — several of the ring styles are two lines long. */

  function bodyOf(button) {
    return document.getElementById(button.getAttribute('aria-controls'));
  }

  function measure(button) {
    const body = bodyOf(button);
    if (!body) return;

    body.classList.add(CLAMP);
    const clipped = body.scrollHeight > body.clientHeight + 1;
    if (!clipped) body.classList.remove(CLAMP);
    button.hidden = !clipped;
  }

  function measureAll(root) {
    (root || document).querySelectorAll(TOGGLE).forEach(measure);
  }

  /* --------------------------------------------------------------- panel */

  function panelOf(grid) {
    let panel = grid.querySelector('.zkg-panel');
    if (panel) return panel;

    panel = document.createElement('div');
    panel.className = 'zkg-panel';
    panel.hidden = true;
    panel.setAttribute('role', 'region');
    panel.setAttribute('tabindex', '-1');
    grid.appendChild(panel);
    return panel;
  }

  function fill(panel, entry, closeLabel) {
    const image = entry.querySelector('.zkg-entry__media img');
    const title = entry.querySelector('.zkg-entry__title');
    const body = entry.querySelector('.zkg-entry__body');

    const inner = document.createElement('div');
    inner.className = 'zkg-panel__inner';

    if (image) {
      const media = document.createElement('div');
      media.className = 'zkg-panel__media';
      const copy = image.cloneNode(false);
      copy.removeAttribute('loading');
      media.appendChild(copy);
      inner.appendChild(media);
    }

    const column = document.createElement('div');
    column.className = 'zkg-panel__copy';

    if (title) {
      const heading = document.createElement('h3');
      heading.className = 'zkg-panel__title';
      heading.textContent = title.textContent.trim();
      column.appendChild(heading);
      panel.setAttribute('aria-label', title.textContent.trim());
    }

    const text = document.createElement('div');
    text.className = 'zkg-entry__body zkg-panel__text';
    text.innerHTML = body ? body.innerHTML : '';
    column.appendChild(text);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'zkg-entry__more zkg-panel__close';
    close.textContent = closeLabel;
    close.addEventListener('click', () => collapse(true));
    column.appendChild(close);

    inner.appendChild(column);
    panel.replaceChildren(inner);
  }

  // The panel belongs after the last card of the clicked card's row, which is
  // whichever cards share its top edge — three across, two, or one, depending
  // on the viewport.
  function endOfRow(entry) {
    const cards = [...entry.parentElement.querySelectorAll('.zkg-entry')];
    const top = entry.offsetTop;
    return cards.reduce((last, card) => (Math.abs(card.offsetTop - top) < 2 ? card : last), entry);
  }

  function place(panel, entry) {
    const anchor = endOfRow(entry);
    if (panel.previousElementSibling !== anchor) {
      anchor.insertAdjacentElement('afterend', panel);
    }
  }

  function slide(panel, to) {
    if (reducedMotion.matches) {
      panel.style.height = to === 'open' ? 'auto' : '';
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Clicking a second card interrupts a collapse that is still running.
      if (panel.zkgTransition) {
        panel.removeEventListener('transitionend', panel.zkgTransition);
        panel.zkgTransition = null;
      }

      const from = to === 'open' ? 0 : panel.scrollHeight;
      const target = to === 'open' ? panel.scrollHeight : 0;

      panel.style.overflow = 'hidden';
      panel.style.height = `${from}px`;
      // Force the start height to take before the transition to the target.
      void panel.offsetHeight;
      panel.style.height = `${target}px`;

      const done = (event) => {
        if (event.propertyName !== 'height') return;
        panel.removeEventListener('transitionend', done);
        panel.zkgTransition = null;
        panel.style.overflow = '';
        panel.style.height = to === 'open' ? 'auto' : '';
        resolve();
      };
      panel.zkgTransition = done;
      panel.addEventListener('transitionend', done);
    });
  }

  function collapse(returnFocus) {
    if (!openButton) return;

    const button = openButton;
    const entry = button.closest('.zkg-entry');
    const panel = entry.parentElement.querySelector('.zkg-panel');
    openButton = null;

    button.setAttribute('aria-expanded', 'false');
    button.textContent = button.dataset.labelMore;
    entry.classList.remove(OPEN);
    if (returnFocus) button.focus();

    if (panel) {
      slide(panel, 'closed').then(() => {
        // Unless this same panel was reopened for another card meanwhile —
        // clicking a second card in the same band collapses and refills it.
        const reused = openButton && openButton.closest('.zkg-entry').parentElement === panel.parentElement;
        if (!reused) panel.hidden = true;
      });
    }
  }

  function expand(button) {
    const entry = button.closest('.zkg-entry');
    const grid = entry.parentElement;
    const panel = panelOf(grid);

    // Measure the rows with the panel out of the way, or a panel already open
    // above this row shifts every top edge below it.
    panel.hidden = true;
    panel.style.height = '';
    place(panel, entry);
    fill(panel, entry, button.dataset.labelLess);
    panel.hidden = false;

    button.setAttribute('aria-expanded', 'true');
    button.textContent = button.dataset.labelLess;
    entry.classList.add(OPEN);
    openButton = button;

    slide(panel, 'open').then(() => {
      const box = panel.getBoundingClientRect();
      if (box.bottom > window.innerHeight && box.height < window.innerHeight) {
        panel.scrollIntoView({ block: 'end', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    });
  }

  /* -------------------------------------------------------------- events */

  document.addEventListener('click', (event) => {
    const button = event.target.closest(TOGGLE);
    if (!button) return;

    if (button === openButton) {
      collapse(false);
      return;
    }

    if (openButton) collapse(false);
    expand(button);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openButton) collapse(true);
  });

  // The clamp is a line count and the panel sits at the end of a row, so both
  // depend on the column count — and on the webfont that lands after the first
  // measurement.
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      measureAll();
      if (openButton) {
        const entry = openButton.closest('.zkg-entry');
        const panel = entry.parentElement.querySelector('.zkg-panel');
        if (panel) {
          panel.hidden = true;
          place(panel, entry);
          panel.hidden = false;
        }
      }
    }, 150);
  });

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => measureAll());

  // Sections re-render as the merchant edits them in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    openButton = null;
    measureAll(event.target);
  });

  measureAll();
})();
